import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Reference } from '@1po/1po-bff-fe-spec/generated/basket/response/model/OtherSection';
import { ReferenceKey } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceKey';
import { RootState } from 'app/AppStore';
import { AngleDownIcon, AngleUpIcon, CheckCircleIcon } from 'assets/icons';
import {
  setReferencesIsSelected,
  updateAllUrgencyFlagsRequest,
  updateOrderMarkRequest,
  updateReferenceUrgencyFlag,
} from 'domains/basket/Basket.store';
import { OtherSectionLocal } from 'domains/basket/Basket.types';
import { collectAllReferences } from 'domains/order/Order.mapper';
import { getOrderMarkMaxLength } from 'domains/order/Order.utils';
import { getDiscounts, getLoadedPrices } from 'domains/references';
import { getUserCommercialLink, getUserCountry } from 'domains/user';
import CartReferenceTable from 'pages/CartPage/CartStep/CartItems/CartReferenceTable';
import { calculateTotalPrice } from 'pages/CartPage/CartStep/CartStep';
import { theme } from 'styles';
import { Checkbox, Flex, Icon, MarginBox, Text } from 'UI';
import { textFormatter } from 'utils/format';
import { CartItemWrapper, SInput, UrgentWrapper } from '../CartItems.styled';

interface CartItemsOthersProps {
  otherSection: OtherSectionLocal;
  currency?: string;
  addedOtherReference?: string;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  highlightedRef: ReferenceKey | undefined;
  setHighlightedRef: (referenceKey: ReferenceKey | undefined) => void;
  markRemovedReferences: (numberOfReferences: number) => void;
  disabledRefs: string[];
  highlightedReference: ReferenceKey | undefined;
  setDisableRefs: (ref: string) => void;
  unsetDisableRef: (ref: string) => void;
}

const CartItemsOthers = ({
  otherSection,
  currency,
  addedOtherReference,
  expanded,
  setExpanded,
  highlightedRef,
  setHighlightedRef,
  markRemovedReferences,
  disabledRefs,
  highlightedReference,
  setDisableRefs,
  unsetDisableRef,
}: CartItemsOthersProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isSelectAllUrgentChecked, setAllUrgentChecked] = useState(false);

  const referenceMap = otherSection.references.reduce(
    (acc, next) => acc.set(next.referenceNumber, next),
    new Map<string, Reference>(),
  );
  const refNumbers = otherSection.references.map((r) => r.referenceNumber);
  const discounts = useSelector((state: RootState) => getDiscounts(state, refNumbers));
  const loadedPrices = useSelector((state: RootState) => getLoadedPrices(state, refNumbers));
  const lastAddedRef = addedOtherReference ? referenceMap.get(addedOtherReference) : undefined;
  const totalPrice = calculateTotalPrice(otherSection?.references, discounts, loadedPrices);
  const commercialLink = useSelector(getUserCommercialLink);
  const userCountry = useSelector(getUserCountry);
  const orderMarkMaxLength = getOrderMarkMaxLength(userCountry);
  const isAllOtherRefUrgent =
    otherSection.references.filter((ref) => ref.isUrgent).length === otherSection.references.length;

  useEffect(() => {
    if (!highlightedRef) return;

    const tableCols = document.getElementById('otherRefTable')?.querySelectorAll('tbody tr td');

    tableCols?.forEach((col) => {
      if (col.innerHTML.includes(highlightedRef.referenceNumber)) {
        col.scrollIntoView({ behavior: 'smooth', block: 'center' });

        col.parentElement?.setAttribute('style', 'box-shadow: 5px 8px 20px rgb(0 0 0 / 30%)');

        const removeHighlight = setTimeout(() => {
          col.parentElement?.setAttribute('style', 'box-shadow: none;');
        }, 5000);

        document.addEventListener(
          'mousedown',
          () => {
            col.parentElement?.setAttribute('style', 'box-shadow: none;');
            clearTimeout(removeHighlight);
          },
          { once: true },
        );
      }
    });
    setHighlightedRef(undefined);
  }, [highlightedRef, lastAddedRef, setHighlightedRef]);

  function selectAllUrgent() {
    dispatch(
      updateAllUrgencyFlagsRequest({
        basketReferenceType: 'OTHER',
        vehicleKey: undefined,
        externalBasketId: undefined,
        isUrgent: !isSelectAllUrgentChecked,
      }),
    );
    dispatch(
      updateReferenceUrgencyFlag({
        vehicleKey: undefined,
        externalBasketId: undefined,
        referencesToUpdate: otherSection.references.map((r) => {
          return {
            referenceNumber: r.referenceNumber,
            sellerNumber: r.sellerNumber,
          };
        }),
        isUrgent: !isSelectAllUrgentChecked,
      }),
    );
    setAllUrgentChecked(!isSelectAllUrgentChecked);
  }

  useEffect(() => {
    setAllUrgentChecked(isAllOtherRefUrgent);
  }, [isAllOtherRefUrgent]);

  function doSelectAll(isSelected: boolean) {
    const otherRefs = collectAllReferences(otherSection?.references);
    dispatch(
      setReferencesIsSelected({
        isSelected,
        referenceIds: otherRefs,
        vehicleKey: undefined,
      }),
    );
  }

  function selectAll(isSelected: boolean) {
    doSelectAll(isSelected);
  }

  const selectedReferencesCount = otherSection.references.filter((reference) => reference.isSelected).length;
  const isSelectAllChecked = selectedReferencesCount > 0 && selectedReferencesCount === otherSection.references.length;

  const setOrderMark = (value: string) => {
    dispatch(
      updateOrderMarkRequest({
        basketReferenceType: 'OTHER',
        vehicleKey: undefined,
        externalBasketId: undefined,
        orderMark: value,
      }),
    );
  };

  return (
    <CartItemWrapper expanded={expanded} data-cy={'cart-items-other-section'}>
      <Flex direction={'row'} minWidth={1000}>
        <MarginBox mt={27} ml={36}>
          <Checkbox checked={isSelectAllChecked} onChange={selectAll} data-cy={'cart-other-section-checkbox'} />
        </MarginBox>
        <MarginBox mt={27} ml={36}>
          <Flex direction={'row'}>
            <Text type="base" data-cy={'other-articles-in-your-cart-text'}>
              {t('cart.other_articles', 'Other articles in your cart')}
            </Text>
          </Flex>
        </MarginBox>
        {commercialLink?.canPlaceUrgentOrders ? (
          <Flex justify={'flex-end'}>
            <MarginBox mr={67} mt={26}>
              <UrgentWrapper onClick={selectAllUrgent}>
                <Flex>
                  <Checkbox checked={isSelectAllUrgentChecked} />
                  <Icon
                    mr={5}
                    ml={5}
                    IconComponent={CheckCircleIcon}
                    size={12}
                    display={'inline'}
                    color={theme.color.brand}
                    background={theme.color.brand_black}
                    round
                  />
                  <Text type="base" color="clear_blue">
                    {t('cart.urgent_delivery', 'Urgent delivery')}
                  </Text>
                </Flex>
              </UrgentWrapper>
              <MarginBox mt={1} ml={20}>
                <Text type={'light_dimmer'}>{t('cart.urgent_delivery_note', '(Select for faster delivery)')}</Text>
              </MarginBox>
            </MarginBox>
          </Flex>
        ) : (
          <Flex />
        )}
        <MarginBox mt={13} mr={36}>
          <Flex direction={'row'}>
            <Text type={'highlight'}>{t('common.price.total_VAT_excl', 'Total VAT. Excl')}</Text>
          </Flex>
          <Flex direction={'row'}>
            <Text type={'highlight'} data-cy={'section-price-vat-excl'}>
              {textFormatter.formatCurrency(totalPrice, currency)}
            </Text>
          </Flex>
        </MarginBox>
        <MarginBox mt={27} mr={36}>
          <Icon IconComponent={expanded ? AngleUpIcon : AngleDownIcon} onClick={() => setExpanded(!expanded)} />
        </MarginBox>
      </Flex>
      {expanded && (
        <Flex direction={'row'}>
          <MarginBox ml={37} mt={17}>
            <SInput
              maxLength={orderMarkMaxLength}
              onBlur={setOrderMark}
              bordered
              initialValue={otherSection.orderMark}
              placeholder={t('order.order_mark', 'Click here to add your order mark')}
              data-cy={'cart-order-mark-input'}
            />
          </MarginBox>
        </Flex>
      )}
      {expanded && (
        <div id={'otherRefTable'}>
          <Flex direction={'row'}>
            <CartReferenceTable
              markRemovedReferences={markRemovedReferences}
              disabledRefs={disabledRefs}
              setDisableRefs={setDisableRefs}
              unsetDisableRef={unsetDisableRef}
              highlightedReference={highlightedReference}
            />
          </Flex>
        </div>
      )}
    </CartItemWrapper>
  );
};

export default CartItemsOthers;
